import React from 'react';
import styled from 'styled-components';

import PillLabel from '@ui/labels/PillLabel.tsx';

import { ParkingLabelStatus, PoiOpenStatus, type PoiOpenStatusType } from '../../../utils/constants.ts';
import { useCSSColors } from '../../hooks/useCSSColors.ts';

const getLabelStyle = (status: string | undefined, colors: Record<string, string>) => {
  switch (status) {
    case ParkingLabelStatus.CLOSED:
      return {
        backgroundColor: colors.statusClosedBackground,
        color: colors.statusClosedText,
      };
    case ParkingLabelStatus.NEARING_CAPACITY:
      return {
        backgroundColor: colors.statusDelayedBackground,
        color: colors.statusDelayedText,
      };
    case ParkingLabelStatus.FULL:
      return {
        backgroundColor: colors.statusCancelledBackground,
        color: colors.statusCancelledText,
      };
    case ParkingLabelStatus.OPEN:
    default:
      return {
        backgroundColor: colors.statusOpenBackground,
        color: colors.statusOpenText,
      };
  }
};

interface PoiStatusPillProps {
  T: (key: string) => string;
  className?: string;
  status?: string;
}

/**
 * Pill-style status label for parking lots.
 * Uses background color + text color for visual distinction.
 */
const PoiStatusPill = ({ T, className, status }: PoiStatusPillProps) => {
  const colors = useCSSColors();
  const pillStyle = getLabelStyle(status, colors);

  const statusText = T(`ui:${status}`);

  return (
    <PillLabel $backgroundColor={pillStyle.backgroundColor} $color={pillStyle.color} className={className}>
      {statusText}
    </PillLabel>
  );
};

export default PoiStatusPill;

// --- POI Open Status Text Component (plain colored bold text, not a pill) ---

const StatusText = styled.span<{ $color: string }>`
  display: block;
  color: ${({ $color }) => $color};
  ${({ theme }) => theme.fontSize('14px')};
  font-weight: bold;
  text-transform: none;
  line-height: 150%;
`;

/**
 * Get the text color for a POI open status.
 *
 * Driven entirely by the locale-independent status enum. Do NOT branch on `timeLabel`:
 * it is translated display text, so substring checks like `timeLabel.includes('Opening')`
 * silently stop working in non-English instances.
 *
 * Uses theme tokens: statusOpenBackground for open (green), errorState for closed (red),
 * statusDelayedBackground for opening/closing soon (orange).
 *
 * Closed deliberately uses `errorState` (the palette's defined red) rather than
 * `statusClosedText`. The latter is the near-black foreground intended to sit on top of the
 * `statusClosedBackground` pill, so as standalone text it just rendered black.
 */
const getOpenStatusColor = (status: PoiOpenStatusType, colors: Record<string, string>): string => {
  switch (status) {
    case PoiOpenStatus.OPENING_SOON:
    case PoiOpenStatus.CLOSING_SOON:
      return colors.statusDelayedBackground;
    case PoiOpenStatus.OPEN:
      return colors.statusOpenBackground;
    default:
      return colors.errorState;
  }
};

/**
 * Get the display text for POI open status.
 *
 * Prefers the already-translated time label ("Closing in 30 minutes") when the plugin
 * supplied one, and falls back to a plain Open/Closed translation otherwise.
 */
const getOpenStatusText = (
  status: PoiOpenStatusType,
  timeLabel: string | undefined,
  T: (key: string) => string,
): string => {
  if (timeLabel) {
    return timeLabel;
  }
  return status === PoiOpenStatus.OPEN || status === PoiOpenStatus.CLOSING_SOON ? T('ui:Open') : T('ui:Closed');
};

interface PoiOpenStatusTextProps {
  T: (key: string) => string;
  className?: string;
  status: PoiOpenStatusType;
  timeLabel?: string;
}

/**
 * Plain text status display for POI open/closed status.
 * Uses colored bold text (no background pill) per design requirements.
 *
 * Style requirement: Plain colored bold text, NOT a background pill/chip.
 * This matches the approved design from PR 217955.
 *
 * Exposes the locale-independent status via `data-status` so end-to-end tests can assert
 * the state without matching the translated, time-dependent label text.
 */
export const PoiOpenStatusText = ({ T, className, status, timeLabel }: PoiOpenStatusTextProps) => {
  const colors = useCSSColors();
  const color = getOpenStatusColor(status, colors);
  const text = getOpenStatusText(status, timeLabel, T);

  return (
    <StatusText $color={color} className={className} data-testid="poiOpenStatus" data-status={status}>
      {text}
    </StatusText>
  );
};
